import {
  CheckboxSelectionCallbackParams,
  RowDataUpdatedEvent,
  RowNode,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Predicates } from "libraries/predicates/predicates";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ICompositionMpnComponent,
  IMpnComposition,
  ITccComposition,
} from "types/data.interface";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";

const ItemNumberValuesImportTable = ({
  isInApprovalProcess,
  isRevision,
  composition,
  isSummaryPage,
  isViewOnly
}: {
  isInApprovalProcess: boolean;
  isRevision: boolean;
  composition: ITccComposition[];
  isSummaryPage?: boolean;
  isViewOnly?: boolean;
}) => {
  const { state, dispatch } = useCreateMpnStore();

  const gridRef = useRef<AgGridReact<ITccComposition>>(null);

  const setSelectedCompositionRows = (value: number[]) => {

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_GP_COMPOSITION_ROWS,
        [],
      ),
    );

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_ITEM_NUMBER_COMPOSITION_ROWS,
        value,
      ),
    );
  };

  const setUsedMoldIndexes = (value: string[]) => {
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_SELECTED_MOLD_INDEXES, value
      ),
    );
  };

  const columnDefs = useMemo(
    () => [
      {
        valueGetter: (params: any) => {
          return params.data?.pch_comnum ?? "";
        },
        headerName: "Component Number",
        maxWidth: 150,
      },
      {
        headerName: "Component Type",
        valueGetter: (params: any) => params.data?.componentType,
      },

      {
        headerName: "Component",
        valueGetter: (params: any) => {
          return params.data.componentType === "Molded"
            ? `${params.data?.mold?.id} - ${params.data?.mold?.name}`
            : `${params.data?.non_mold?.id} - ${params.data?.non_mold?.name}`;
        },
      },
      {
        headerName: "Marketing Material type",
        valueGetter: (params: any) => params.data?.mktMaterial,
      },
      {
        headerName: "Color",
        valueGetter: (params: any) =>
          params.data?.color?.id
            ? `${params.data?.color?.id} - ${params.data?.color?.name}`
            : "",
      },
      // {
      //   headerName: "Component Non Molded",
      //   valueGetter: (params: any) => {
      //     return params.data?.componentType === "NonMolded"
      //       ? `${params.data?.nonMold?.sequence_number} - ${params.data?.nonMold?.name}`
      //       : "";
      //   },
      // },
      {
        headerName: "Component mandatory (Y/N)",
        valueGetter: (params: any) => params.data?.isMandatory ?? "N",
      },
      {
        headerName: "Component pieces",
        valueGetter: (params: any) => {
          return params.data?.nr_pcs || params.data?.pcs;
        },
      },
      {
        headerName: "Select",
        sortable: false,
        maxWidth: 100,
        checkboxSelection: (
          params: CheckboxSelectionCallbackParams<ITccComposition>,
        ) => checkIfIsSelectable(params?.node),
        showDisabledCheckboxes: isViewOnly,
        hide: isSummaryPage || isViewOnly,
      },
    ],
    [
      isInApprovalProcess,
      isRevision,
      state?.selectedItemNumberCompositionRows,
      state?.selectedMoldIndexes,
      composition,
    ],
  );

  const defaultColDef = useMemo(
    () => ({
      sortingOrder: ["asc" as const, "desc" as const],
      minWidth: 100,
      sortable: true,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      suppressMenu: true,
      suppressMovable: true,
      icons: {
        sortAscending: "<i class='fa fa-sort-up'/>",
        sortDescending: "<i class='fa fa-sort-down'/>",
        sortUnSort: "<i class='fa fa-sort' style='color:#e3e6f0'></i>",
        filter: "<i class='fa fa-filter'></i>",
      },
    }),
    [],
  );

  const handleOnSelectionChanged = useCallback(() => {
    const selectedRows = Array.from(
      new Set(gridRef?.current?.api.getSelectedRows()),
    );

    const mpnComp: IMpnComposition = {
      ...state?.composition,
      molded_components: [],
      non_molded_components: [],
      mpns: [],
      skus: [],
    };

    selectedRows.forEach((comp: ITccComposition) => {
      const isMoldKey = comp.componentType === "Molded" ? "mold" : "nonMold";

      const existingMolded = state.composition.molded_components.find(
        (c: any) => String(c.mold_id) === String(comp.mold?.id)
      );


      const mappedComp: ICompositionMpnComponent = {
        // artwork_description: "",
        // artwork_id: 0,
        // color_description: comp?.color ? comp.color.name : "",
        // color_id: comp?.color ? comp.color.id : "",
        color_family_name: comp?.color ? comp.color?.color_family_name : "",
        // decoration_technique_description: "",
        // decoration_technique_id: 0,
        full_objects: {
          artwork: null,
          color: null,
          decoration_technique: null,
          material: null,
          mold: null,
          non_mold: null,
        },
        importedTccCompositionId: 0,
        // is_tps: false,
        // material_description: comp.nonMold ? comp.nonMold.material.name : "",
        // material_id: comp.nonMold
        //   ? Number(comp.nonMold.material.four_digit_code)
        //   : 0,
        material_id: existingMolded?.material_id ?? 0,
        material_description: existingMolded?.material_description ?? "",
        color_id: existingMolded?.color_id ?? "",
        color_description: existingMolded?.color_description ?? "",
        decoration_technique_id: existingMolded?.decoration_technique_id ?? 0,
        decoration_technique_description:
          existingMolded?.decoration_technique_description ?? "",
        artwork_id: existingMolded?.artwork_id ?? 0,
        artwork_description: existingMolded?.artwork_description ?? "",
        is_tps: existingMolded?.is_tps ?? false,
        //@ts-ignore
        mold_description: !!comp[isMoldKey] ? comp[isMoldKey]?.name : isMoldKey === "nonMold" && comp.non_mold?.name,
        //@ts-ignore
        mold_id: !!comp[isMoldKey] ? comp[isMoldKey]?.id : comp[isMoldKey],
        //@ts-ignore
        sequence_number: Number(comp.nonMold?.sequence_number) || comp.non_mold?.id,
        mold_index: 0,
        node_id: comp.id,
        nr_pieces: Number(comp.pcs),
        quantity: 1,
        type: comp.componentType,
        componentTypeOf: comp?.componentTypeOf,
        isMandatory: comp?.isMandatory,
        mktMaterial: comp?.mktMaterial,
      };

      if (mappedComp.type === "Molded") {
        mpnComp.molded_components.push(mappedComp);
        return;
      }
      mpnComp.non_molded_components.push(mappedComp);
    });

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_COMPOSITION,
        mpnComp,
      ),
    );

    setSelectedCompositionRows(
      gridRef?.current?.api.getSelectedRows().map((row) => row.id) ?? [],
    );

    setUsedMoldIndexes(
      Array.from(
        new Set(
          gridRef?.current?.api
            .getSelectedRows()
            .filter((composition) =>
              Predicates.isNotNullAndNotUndefinedAndNotEmpty(composition.index),
            )
            .map((composition) => composition.index ?? ""),
        ),
      ) ?? [],
    );

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_IS_CHANGED_IMPORTED_VALUES,
        true,
      ),
    );
  }, []);

  const checkIfIsSelectable = (rowNode: RowNode) => {
    return (
      state.selectedGpCompositionRows.length < 1 &&
      !isInApprovalProcess &&
      !isRevision &&
      (Predicates.isNullOrUndefinedOrEmpty(rowNode.data?.index) ||
        Predicates.isNotNullAndNotUndefined(
          state?.selectedItemNumberCompositionRows.find((id) => id === rowNode.data?.id),
        ) ||
        !state?.selectedMoldIndexes.includes(rowNode.data?.index ?? ""))
    );
  };

  const onRowDataUpdated = (params: RowDataUpdatedEvent<ITccComposition>) => {
    params.api.forEachNode((rowNode: RowNode) => {
      rowNode.setSelected(
        Predicates.isNotNullAndNotUndefined(
          state?.selectedItemNumberCompositionRows.find(
            (id) => id === rowNode.data?.id,
          ),
        ),
        false,
        true,
      );
    });
  };

  useEffect(() => {
    const updateSelectable = (rowNode: RowNode) => {
      rowNode.setRowSelectable(checkIfIsSelectable(rowNode));
    };

    if (gridRef.current?.api) {
      gridRef?.current?.api.forEachNode(updateSelectable);
    }
  }, [state?.selectedMoldIndexes]);

  return (
    <>
      <div className="ag-theme-alpine">
        <AgGridReact
          domLayout="autoHeight"
          ref={gridRef}
          rowData={composition}
          getRowId={(params) => String(params.data.id)}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressRowClickSelection
          unSortIcon
          suppressPaginationPanel
          rowSelection="multiple"
          onRowDataUpdated={onRowDataUpdated}
          onSelectionChanged={() => handleOnSelectionChanged()}
        ></AgGridReact>
      </div>
    </>
  );
};

export default ItemNumberValuesImportTable;
